import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Badge,
  Table,
  Modal,
  Alert,
  Spinner,
  InputGroup,
  Nav,
  Tab,
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";
import { FaEye, FaEdit, FaCheck, FaTimes, FaDownload } from "react-icons/fa";

export default function AdminEventOrderRequestManagement() {
  const { currentUser } = useAuth();
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [event, setEvent] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOrderId, setFilterOrderId] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState("orders");

  // Edit form state
  const [editForm, setEditForm] = useState({
    status: "",
    statusMessage: "",
    amount: 0,
    userEmail: "",
    uniqueId: "",
    formSubmissionId: "",
    requestId: "",
  });

  useEffect(() => {
    checkAdminAccess();
  }, [currentUser]);

  useEffect(() => {
    if (isAdmin && eventId) {
      loadEventData();
      loadOrders();
      loadRequests();
    }
  }, [isAdmin, eventId]);

  useEffect(() => {
    if (isAdmin && eventId) {
      loadRequests();
    }
  }, [filterOrderId]);

  const checkAdminAccess = async () => {
    if (!currentUser) {
      navigate("/");
      return;
    }

    try {
      const adminDoc = await fires.collection("admin").doc(currentUser.uid).get();
      if (adminDoc.exists) {
        setIsAdmin(true);
      } else {
        navigate("/");
      }
    } catch (err) {
      console.error("Error checking admin access:", err);
      navigate("/");
    }
  };

  const loadEventData = async () => {
    try {
      const eventDoc = await fires.collection("events").doc(eventId).get();
      if (eventDoc.exists) {
        setEvent({
          id: eventDoc.id,
          ...eventDoc.data(),
        });
      } else {
        setError("Event not found");
      }
    } catch (err) {
      setError("Failed to load event: " + err.message);
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      const ordersSnapshot = await fires
        .collection("orders")
        .where("eventId", "==", eventId)
        .orderBy("timestamp", "desc")
        .get();

      const ordersData = [];
      ordersSnapshot.docs.forEach((doc) => {
        const orderData = doc.data();
        ordersData.push({
          id: doc.id,
          ...orderData,
          createdAt: orderData.timestamp?.toDate?.() || new Date(),
        });
      });

      setOrders(ordersData);
    } catch (err) {
      setError("Failed to load orders: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      let query = fires
        .collection("events")
        .doc(eventId)
        .collection("requests");

      // If filterOrderId is set, filter by orderId first, then order by timestamp
      if (filterOrderId) {
        query = query
          .where("orderId", "==", filterOrderId)
          .orderBy("timestamp", "desc");
      } else {
        query = query.orderBy("timestamp", "desc");
      }

      const requestsSnapshot = await query.get();
      const requestsData = [];
      requestsSnapshot.docs.forEach((doc) => {
        const requestData = doc.data();
        requestsData.push({
          id: doc.id,
          ...requestData,
          createdAt: requestData.timestamp?.toDate?.() || new Date(),
        });
      });

      setRequests(requestsData);
    } catch (err) {
      console.error("Failed to load requests: " + err.message);
      // If error is about missing index, show a helpful message
      if (err.message.includes("index")) {
        setError(
          "Failed to load requests: Firestore index required. Please create a composite index for 'orderId' and 'timestamp' fields."
        );
      } else {
        setError("Failed to load requests: " + err.message);
      }
    }
  };

  // Get formSubmissionId from request (requestData.formSubmissionId or formSubmissionId at top level)
  const getRequestFormSubmissionId = (request) => {
    if (!request) return null;
    return request.requestData?.formSubmissionId || request.formSubmissionId || null;
  };

  // Match orders with requests: use orderId as primary reference (request.orderId === order.uniqueId or order.id)
  const getOrderRequestMatch = (order) => {
    let matchedRequest = null;

    // Primary: find request by orderId (request.orderId references this order)
    matchedRequest = requests.find(
      (r) => r.orderId === order.uniqueId || r.orderId === order.id
    );

    // Fallback: find by requestId on order
    if (!matchedRequest && order.requestId) {
      matchedRequest = requests.find((r) => r.id === order.requestId);
    }

    // Fallback: find by formSubmissionId
    if (!matchedRequest && order.formSubmissionId) {
      matchedRequest = requests.find(
        (r) => getRequestFormSubmissionId(r) === order.formSubmissionId
      );
    }

    const requestFormId = matchedRequest ? getRequestFormSubmissionId(matchedRequest) : null;

    // Check if order needs highlighting (request has formSubmissionId but order doesn't)
    const needsHighlight =
      matchedRequest &&
      matchedRequest.type === "user-request" &&
      requestFormId &&
      !order.formSubmissionId;

    return {
      request: matchedRequest,
      requestFormSubmissionId: requestFormId,
      needsHighlight,
    };
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    const match = getOrderRequestMatch(order);
    setEditForm({
      status: order.status || "pending",
      statusMessage: order.statusMessage || "",
      amount: order.amount || 0,
      userEmail: order.userEmail || "",
      uniqueId: order.uniqueId || "",
      formSubmissionId: order.formSubmissionId || getRequestFormSubmissionId(match.request) || "",
      requestId: order.requestId || match.request?.id || "",
    });
    setShowEditModal(true);
  };

  const handleSaveOrder = async () => {
    if (!selectedOrder) return;

    setSaving(true);
    try {
      const updateData = {
        status: editForm.status,
        statusMessage: editForm.statusMessage,
        amount: parseFloat(editForm.amount) || 0,
        userEmail: editForm.userEmail,
        uniqueId: editForm.uniqueId,
        updatedAt: new Date(),
        updatedBy: currentUser.uid,
      };

      // Only update formSubmissionId and requestId if they are provided
      if (editForm.formSubmissionId) {
        updateData.formSubmissionId = editForm.formSubmissionId;
      }
      if (editForm.requestId) {
        updateData.requestId = editForm.requestId;
      }

      await fires.collection("orders").doc(selectedOrder.id).update(updateData);
      
      // Reload orders and requests
      await loadOrders();
      await loadRequests();
      setShowEditModal(false);
      setSelectedOrder(null);
    } catch (err) {
      alert("Failed to update order: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      completed: "success",
      pending: "warning",
      cancelled: "danger",
      refunded: "info",
      "email-sent": "primary",
    };

    return (
      <Badge bg={statusColors[status] || "secondary"}>
        {status || "pending"}
      </Badge>
    );
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = date instanceof Date ? date : date.toDate();
    return d.toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const hasNoForm = (request) => {
    return !request.form || 
      (typeof request.form === 'object' && Object.keys(request.form).length === 0) || 
      (typeof request.form === 'string' && request.form.trim() === '');
  };

  const downloadRequestsCSV = (onlyWithoutForm = false) => {
    let requestsToDownload = requests;
    
    if (onlyWithoutForm) {
      requestsToDownload = requests.filter(hasNoForm);
      if (requestsToDownload.length === 0) {
        alert("No requests without forms to download");
        return;
      }
    } else {
      if (requests.length === 0) {
        alert("No requests to download");
        return;
      }
    }

    // Define CSV headers
    const headers = [
      "Request ID",
      "Email",
      "User Email",
      "Order ID",
      "Type",
      "Request Type",
      "Status",
      "Created Date",
      "Has Form",
      "Form Submission ID",
    ];

    // Convert requests to CSV rows
    const csvRows = requestsToDownload.map((request) => {
      const hasForm = !hasNoForm(request);
      
      return [
        request.id || "",
        request.email || "",
        request.userEmail || "",
        request.orderId || "",
        request.type || "",
        request.requestType || "",
        request.status || "pending",
        request.createdAt ? formatDate(request.createdAt) : "",
        hasForm ? "Yes" : "No",
        request.formSubmissionId || "",
      ].map((field) => {
        // Escape commas and quotes in CSV
        const stringField = String(field || "");
        if (stringField.includes(",") || stringField.includes('"') || stringField.includes("\n")) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      });
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...csvRows.map((row) => row.join(",")),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    const filename = onlyWithoutForm 
      ? `event-${eventId}-requests-without-form-${new Date().toISOString().split('T')[0]}.csv`
      : `event-${eventId}-requests-${new Date().toISOString().split('T')[0]}.csv`;
    
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      !searchTerm ||
      order.uniqueId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.userEmail?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus =
      filterStatus === "all" || order.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Orders for this event that don't have formSubmissionId
  const ordersWithoutFormSubmissionId = orders.filter(
    (order) => !order.formSubmissionId || String(order.formSubmissionId).trim() === ""
  );

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading orders...</p>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="py-5 text-center">
        <Alert variant="danger">Unauthorized access</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="mb-1">Event Order Request Management</h3>
              {event && (
                <p className="text-muted mb-0">
                  Event: <strong>{event.title || eventId}</strong>
                </p>
              )}
            </div>
            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger">{error}</Alert>
          </Col>
        </Row>
      )}

      {/* Tabs */}
      <Tab.Container defaultActiveKey="orders">
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="orders" onClick={() => setActiveTab("orders")}>
              Orders ({orders.length})
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="requests" onClick={() => setActiveTab("requests")}>
              Requests ({requests.length})
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Orders Tab */}
          <Tab.Pane eventKey="orders">
            {/* Filters for Orders */}
            <Row className="mb-3">
              <Col md={6}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search by Order ID or Email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="email-sent">Email Sent</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="refunded">Refunded</option>
                </Form.Select>
              </Col>
              <Col md={3} className="text-end">
                <Button variant="outline-primary" onClick={() => { loadOrders(); loadRequests(); }}>
                  Refresh
                </Button>
              </Col>
            </Row>

            {/* Summary Cards */}
            <Row className="mb-4">
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <Card.Title className="text-muted small">Total Orders</Card.Title>
                    <Card.Text className="h4 mb-0">{orders.length}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <Card.Title className="text-muted small">Without Form ID</Card.Title>
                    <Card.Text className="h4 mb-0 text-warning">
                      {ordersWithoutFormSubmissionId.length}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <Card.Title className="text-muted small">Total Revenue</Card.Title>
                    <Card.Text className="h4 mb-0 text-success">
                      {formatCurrency(
                        orders.reduce((sum, order) => sum + (order.amount || 0), 0)
                      )}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <Card.Title className="text-muted small">Pending Orders</Card.Title>
                    <Card.Text className="h4 mb-0 text-warning">
                      {orders.filter((o) => o.status === "pending").length}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <Card.Title className="text-muted small">Completed Orders</Card.Title>
                    <Card.Text className="h4 mb-0 text-success">
                      {orders.filter((o) => o.status === "completed").length}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Orders without formSubmissionId */}
            {ordersWithoutFormSubmissionId.length > 0 && (
              <Card className="mb-4 border-warning">
                <Card.Header className="bg-warning text-dark">
                  <strong>Orders Without Form Submission ID ({ordersWithoutFormSubmissionId.length})</strong>
                </Card.Header>
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>User Email</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Request ID</th>
                        <th>Request formSubmissionId</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordersWithoutFormSubmissionId.map((order) => {
                        const match = getOrderRequestMatch(order);
                        return (
                          <tr key={order.id} style={{ backgroundColor: "#fff3cd" }}>
                            <td>
                              <Link
                                to={`/order/${order.uniqueId || order.id}`}
                                className="text-decoration-none"
                                target="_blank"
                              >
                                {order.uniqueId || order.id.substring(0, 12)}...
                              </Link>
                            </td>
                            <td>{order.userEmail || "N/A"}</td>
                            <td className="fw-bold text-success">
                              {formatCurrency(order.amount)}
                            </td>
                            <td>{getStatusBadge(order.status)}</td>
                            <td>{formatDate(order.createdAt)}</td>
                            <td>
                              {match.request ? (
                                <code title={match.request.id}>{match.request.id}</code>
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td>
                              {match.requestFormSubmissionId ? (
                                <code title={match.requestFormSubmissionId}>
                                  {match.requestFormSubmissionId.length > 20
                                    ? `${match.requestFormSubmissionId.substring(0, 20)}...`
                                    : match.requestFormSubmissionId}
                                </code>
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleEditOrder(order)}
                              >
                                <FaEdit /> Edit
                              </Button>
                              {match.requestFormSubmissionId && (
                                <Button
                                  size="sm"
                                  variant="warning"
                                  className="ms-1"
                                  onClick={async () => {
                                    if (
                                      window.confirm(
                                        `Update order with formSubmissionId from request?\n\nRequest Form ID: ${match.requestFormSubmissionId}`
                                      )
                                    ) {
                                      try {
                                        await fires
                                          .collection("orders")
                                          .doc(order.id)
                                          .update({
                                            formSubmissionId: match.requestFormSubmissionId,
                                            requestId: match.request.id,
                                            updatedAt: new Date(),
                                            updatedBy: currentUser.uid,
                                          });
                                        await loadOrders();
                                        alert("Order updated successfully!");
                                      } catch (err) {
                                        alert("Failed to update order: " + err.message);
                                      }
                                    }
                                  }}
                                >
                                  Add Form ID
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* Orders Table */}
            <Card>
              <Card.Body>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>User Email</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-4">
                          No orders found
                        </td>
                      </tr>
                    ) : (
                      filteredOrders.map((order) => {
                        const match = getOrderRequestMatch(order);
                        const rowStyle = match.needsHighlight
                          ? { backgroundColor: "#fff3cd", borderLeft: "4px solid #ffc107" }
                          : {};

                        return (
                          <tr key={order.id} style={rowStyle}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <Link
                                  to={`/order/${order.uniqueId || order.id}`}
                                  className="text-decoration-none"
                                  target="_blank"
                                >
                                  {order.uniqueId || order.id.substring(0, 12)}...
                                </Link>
                                {match.needsHighlight && (
                                  <Badge bg="warning" title="Missing formSubmissionId - Request has it">
                                    ⚠ Missing formSubmissionId
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td>{order.userEmail || "N/A"}</td>
                            <td className="fw-bold text-success">
                              {formatCurrency(order.amount)}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1">
                                {getStatusBadge(order.status)}
                                {match.request && (
                                  <Badge bg="info" style={{ fontSize: "10px" }}>
                                    Request: {match.request.type || "N/A"}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <small>{formatDate(order.createdAt)}</small>
                                {match.request && match.requestFormSubmissionId && (
                                  <small className="text-muted">
                                    Form ID: <code>{match.requestFormSubmissionId.substring(0, 8)}...</code>
                                  </small>
                                )}
                                {order.formSubmissionId && (
                                  <small className="text-success">
                                    Order has Form ID: <code>{order.formSubmissionId.substring(0, 8)}...</code>
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleEditOrder(order)}
                                >
                                  <FaEdit /> Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  as={Link}
                                  to={`/order/${order.uniqueId || order.id}`}
                                  target="_blank"
                                >
                                  <FaEye /> View
                                </Button>
                                {match.needsHighlight && match.request && 
                                 order.formSubmissionId !== match.requestFormSubmissionId && (
                                  <Button
                                    size="sm"
                                    variant="warning"
                                    onClick={async () => {
                                      if (
                                        window.confirm(
                                          `Update order with formSubmissionId from request?\n\nRequest Form ID: ${match.requestFormSubmissionId}`
                                        )
                                      ) {
                                        try {
                                          await fires
                                            .collection("orders")
                                            .doc(order.id)
                                            .update({
                                              formSubmissionId: match.requestFormSubmissionId,
                                              requestId: match.request.id,
                                              updatedAt: new Date(),
                                              updatedBy: currentUser.uid,
                                            });
                                          await loadOrders();
                                          alert("Order updated successfully!");
                                        } catch (err) {
                                          alert("Failed to update order: " + err.message);
                                        }
                                      }
                                    }}
                                  >
                                    Add Form ID
                                  </Button>
                                )}
                                {match.needsHighlight && match.request && 
                                 order.formSubmissionId === match.requestFormSubmissionId && (
                                  <Badge bg="success" style={{ fontSize: "11px", padding: "4px 8px" }}>
                                    ✓ Form ID Already Set
                                  </Badge>
                                )}
                                {match.request && 
                                 match.requestFormSubmissionId && 
                                 (match.request.type?.toLowerCase().includes("join") || 
                                  match.request.requestType?.toLowerCase().includes("join")) && 
                                 order.formSubmissionId !== match.requestFormSubmissionId && (
                                  <Button
                                    size="sm"
                                    variant="success"
                                    onClick={async () => {
                                      if (
                                        window.confirm(
                                          `Update order with formSubmissionId from join request?\n\nRequest Form ID: ${match.requestFormSubmissionId}\nRequest Type: ${match.request.type || match.request.requestType}`
                                        )
                                      ) {
                                        try {
                                          await fires
                                            .collection("orders")
                                            .doc(order.id)
                                            .update({
                                              formSubmissionId: match.requestFormSubmissionId,
                                              updatedBy: currentUser.uid,
                                            });
                                          await loadOrders();
                                          alert("Order updated successfully with formSubmissionId from join request!");
                                        } catch (err) {
                                          alert("Failed to update order: " + err.message);
                                        }
                                      }
                                    }}
                                  >
                                    Update Form ID (Join Request)
                                  </Button>
                                )}
                                {match.request && 
                                 match.requestFormSubmissionId && 
                                 (match.request.type?.toLowerCase().includes("join") || 
                                  match.request.requestType?.toLowerCase().includes("join")) && 
                                 order.formSubmissionId === match.requestFormSubmissionId && (
                                  <Badge bg="success" style={{ fontSize: "11px", padding: "4px 8px" }}>
                                    ✓ Form ID Already Set
                                  </Badge>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Requests Tab */}
          <Tab.Pane eventKey="requests">
            {/* Filters for Requests */}
            <Row className="mb-3">
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>Filter by Order ID</InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Enter Order ID to filter requests..."
                    value={filterOrderId}
                    onChange={(e) => setFilterOrderId(e.target.value)}
                  />
                  {filterOrderId && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setFilterOrderId("")}
                    >
                      Clear
                    </Button>
                  )}
                </InputGroup>
              </Col>
              <Col md={6} className="text-end">
                <Button 
                  variant="success" 
                  className="me-2"
                  onClick={downloadRequestsCSV}
                  disabled={requests.length === 0}
                >
                  <FaDownload className="me-2" />
                  Download CSV
                </Button>
                <Button variant="outline-primary" onClick={loadRequests}>
                  Refresh Requests
                </Button>
              </Col>
            </Row>

            {/* Requests without form field or form is empty */}
            {requests.filter(hasNoForm).length > 0 && (
              <Card className="mb-4 border-warning">
                <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                  <strong>Requests Without Form ({requests.filter(hasNoForm).length})</strong>
                  <Button
                    variant="dark"
                    size="sm"
                    onClick={() => downloadRequestsCSV(true)}
                  >
                    <FaDownload className="me-2" />
                    Download Without Form
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Request ID</th>
                        <th>Email</th>
                        <th>Order ID</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests
                        .filter(hasNoForm)
                        .map((request) => (
                          <tr key={request.id} style={{ backgroundColor: "#fff3cd" }}>
                            <td>
                              <code>{request.id.substring(0, 12)}...</code>
                            </td>
                            <td>
                              <strong>{request.email || request.userEmail || "N/A"}</strong>
                            </td>
                            <td>
                              {request.orderId ? (
                                <Link
                                  to={`/order/${request.orderId}`}
                                  className="text-decoration-none"
                                  target="_blank"
                                >
                                  {request.orderId}
                                </Link>
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td>
                              <Badge bg="info">
                                {request.type || request.requestType || "N/A"}
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                bg={
                                  request.status === "approved"
                                    ? "success"
                                    : request.status === "rejected"
                                    ? "danger"
                                    : "warning"
                                }
                              >
                                {request.status || "pending"}
                              </Badge>
                            </td>
                            <td>{formatDate(request.createdAt)}</td>
                            <td>
                              {request.orderId && (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => {
                                    const order = orders.find(
                                      (o) =>
                                        o.uniqueId === request.orderId ||
                                        o.id === request.orderId
                                    );
                                    if (order) {
                                      handleEditOrder(order);
                                    } else {
                                      alert(
                                        `Order ${request.orderId} not found in orders list`
                                      );
                                    }
                                  }}
                                >
                                  <FaEdit /> Edit Order
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* All Requests Table */}
            <Card>
              <Card.Header>
                <strong>All Requests ({requests.length})</strong>
              </Card.Header>
              <Card.Body>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Request ID</th>
                      <th>Email</th>
                      <th>Order ID</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          {filterOrderId
                            ? `No requests found for Order ID: ${filterOrderId}`
                            : "No requests found"}
                        </td>
                      </tr>
                    ) : (
                      requests.map((request) => {
                        const requestHasNoForm = hasNoForm(request);
                        return (
                          <tr key={request.id} style={requestHasNoForm ? { backgroundColor: "#fff3cd" } : {}}>
                            <td>
                              <code>{request.id.substring(0, 12)}...</code>
                            </td>
                            <td>
                              <strong>{request.email || request.userEmail || "N/A"}</strong>
                            </td>
                            <td>
                              {request.orderId ? (
                                <Link
                                  to={`/order/${request.orderId}`}
                                  className="text-decoration-none"
                                  target="_blank"
                                >
                                  {request.orderId}
                                </Link>
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td>
                              <Badge bg="info">
                                {request.type || request.requestType || "N/A"}
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                bg={
                                  request.status === "approved"
                                    ? "success"
                                    : request.status === "rejected"
                                    ? "danger"
                                    : "warning"
                                }
                              >
                                {request.status || "pending"}
                              </Badge>
                            </td>
                            <td>{formatDate(request.createdAt)}</td>
                            <td>
                              {request.orderId && (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => {
                                    const order = orders.find(
                                      (o) =>
                                        o.uniqueId === request.orderId ||
                                        o.id === request.orderId
                                    );
                                    if (order) {
                                      handleEditOrder(order);
                                    } else {
                                      alert(
                                        `Order ${request.orderId} not found in orders list`
                                      );
                                    }
                                  }}
                                >
                                  <FaEdit /> Edit Order
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Order ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.uniqueId}
                      onChange={(e) =>
                        setEditForm({ ...editForm, uniqueId: e.target.value })
                      }
                      placeholder="Order ID"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>User Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={editForm.userEmail}
                      onChange={(e) =>
                        setEditForm({ ...editForm, userEmail: e.target.value })
                      }
                      placeholder="user@example.com"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={editForm.status}
                      onChange={(e) =>
                        setEditForm({ ...editForm, status: e.target.value })
                      }
                    >
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="email-sent">Email Sent</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="refunded">Refunded</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Amount</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={editForm.amount}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          amount: parseFloat(e.target.value) || 0,
                        })
                      }
                      placeholder="0.00"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Status Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editForm.statusMessage}
                  onChange={(e) =>
                    setEditForm({ ...editForm, statusMessage: e.target.value })
                  }
                  placeholder="Status message (e.g., Order ticket has been sent to email)"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Form Submission ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.formSubmissionId}
                      onChange={(e) =>
                        setEditForm({ ...editForm, formSubmissionId: e.target.value })
                      }
                      placeholder="Form Submission ID"
                    />
                    <Form.Text className="text-muted">
                      Link this order to a form submission
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Request ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.requestId}
                      onChange={(e) =>
                        setEditForm({ ...editForm, requestId: e.target.value })
                      }
                      placeholder="Request ID"
                    />
                    <Form.Text className="text-muted">
                      Link this order to a request
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Alert variant="info" className="mb-0">
                <strong>Original Order ID:</strong> {selectedOrder.uniqueId || selectedOrder.id}
                <br />
                <strong>Original Amount:</strong> {formatCurrency(selectedOrder.amount)}
                <br />
                <strong>Original Status:</strong> {selectedOrder.status || "N/A"}
                <br />
                <strong>Original Form Submission ID:</strong> {selectedOrder.formSubmissionId || "None"}
                <br />
                <strong>Original Request ID:</strong> {selectedOrder.requestId || "None"}
              </Alert>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEditModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveOrder} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FaCheck /> Save Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

